import {
    AppBar,
    Toolbar,
    IconButton,
    Drawer,
    List,
    ListItem,
    ListItemText,
    Button,
    Box,
    Select,
    MenuItem,
    Switch,
    Typography,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { useThemeContext } from '../context/ThemeContext';

const navItems = [
    { label: 'Home', path: '/' },
    { label: 'About', path: '/about' },
    { label: 'Services', path: '/services' },
    { label: 'Contact', path: '/contact' },
];

export default function Navbar() {
    const location = useLocation(); // ✅ current path
    const [open, setOpen] = useState(false);
    const { theme, mode, switchTheme, toggleMode } = useThemeContext();

    const isActive = (path) => location.pathname === path;

    const toggleDrawer = (state) => () => setOpen(state);

    const activeColor = theme.primary || '#42a5f5'; // Fallback blue
    const inactiveColor = theme.text;
    const [logoKey, setLogoKey] = useState(0);

    // Update key whenever route changes
    useEffect(() => {
        setLogoKey((prev) => prev + 1);
    }, [location.pathname]);


    return (
        <>
            <AppBar
                position="static"
                elevation={0}
                sx={{
                    backgroundColor: theme.background,
                    color: theme.text,
                    transition: 'background-color 0.3s ease',
                }}
            >
                <Toolbar sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    {/* Logo */}
                    <Box
                        sx={{
                            position: 'relative',
                            height: 0, // Don't push AppBar height
                            overflow: 'visible',
                        }}
                    >
                        {/* Desktop Logo */}
                        <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                            <motion.img
                                key={logoKey}
                                src="/HOT_logo.png"
                                alt="House of Technology Logo"
                                initial={{ opacity: 0, scale: 0.6, y: -20 }}
                                animate={{ opacity: 1, scale: 1, y: 0 }}
                                transition={{ duration: 0.6, ease: 'easeOut' }}
                                style={{
                                    height: 110,
                                    maxWidth: 140,
                                    position: 'absolute',
                                    top: '-20px', // Shift upward without increasing AppBar height
                                    left: 0,
                                    zIndex: 10,
                                }}
                            />
                        </Box>

                        {/* Mobile Logo */}
                        <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
                            <motion.img
                                key={logoKey}
                                src="/HOT_logo.png"
                                alt="HOT Mini Logo"
                                initial={{ opacity: 0, scale: 0.6, y: -10 }}
                                animate={{ opacity: 1, scale: 1, y: 0 }}
                                transition={{ duration: 0.5, ease: 'easeOut' }}
                                style={{
                                    height: 80,
                                    maxWidth: 100,
                                    position: 'absolute',
                                    top: '-20px', // Also float above on mobile
                                    left: 0,
                                    zIndex: 10,
                                }}
                            />
                        </Box>
                    </Box>



                    {/* Desktop Navigation */}
                    <Box sx={{ display: { xs: 'none', sm: 'flex' }, gap: 2, alignItems: 'center' }}>
                        {navItems.map((item) => (
                            <Button
                                key={item.label}
                                component={Link}
                                to={item.path}
                                sx={{
                                    color: isActive(item.path) ? theme.text : `${theme.text}AA`,
                                    fontWeight: isActive(item.path) ? 700 : 400,
                                    backgroundColor: isActive(item.path) ? `${theme.primary}22` : 'transparent',
                                    borderRadius: 2,
                                    px: 2,
                                    py: 1,
                                    transition: 'all 0.3s ease-in-out',
                                    '&:hover': {
                                        backgroundColor: `${theme.primary}33`,
                                    },
                                }}
                            >
                                {item.label}
                            </Button>
                        ))}
                        {/* Mode Toggle */}
                        <Box sx={{ display: 'flex', alignItems: 'center', ml: 2 }}>
                            <Typography variant="body2" color={theme.text}>
                                {mode === 'light' ? '🌞' : '🌙'}
                            </Typography>
                            <Switch checked={mode === 'dark'} onChange={toggleMode} color="default" />
                        </Box>
                    </Box>

                    {/* Mobile Menu Icon */}
                    <IconButton sx={{ display: { sm: 'none' }, color: theme.text }} onClick={toggleDrawer(true)}>
                        <MenuIcon />
                    </IconButton>
                </Toolbar>
            </AppBar>

            {/* Mobile Drawer */}
            <Drawer anchor="right" open={open} onClose={toggleDrawer(false)}>
                <Box
                    sx={{
                        width: 250,
                        backgroundColor: theme.background,
                        height: '100%',
                        color: theme.text,
                    }}
                >
                    <List>
                        {navItems.map((item) => (
                            <ListItem
                                button
                                key={item.label}
                                component={Link}
                                to={item.path}
                                onClick={toggleDrawer(false)}
                                sx={{
                                    color: isActive(item.path) ? theme.text : `${theme.text}AA`,
                                    fontWeight: isActive(item.path) ? 700 : 400,
                                    backgroundColor: isActive(item.path) ? `${theme.primary}22` : 'transparent',
                                    borderRadius: 1,
                                    mx: 1,
                                    my: 0.5,
                                }}
                            >
                                <ListItemText primary={item.label} />
                            </ListItem>
                        ))}
                    </List>

                    <Box sx={{ p: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Typography variant="body2">Dark Mode</Typography>
                            <Switch checked={mode === 'dark'} onChange={toggleMode} />
                        </Box>
                    </Box>
                </Box>
            </Drawer>
        </>
    );
}
